/*** Fabric Inspection ***/

import { TFilterSelected } from '../filter.types';
import { TPaginationInfo } from '../input-app/fabric-inspection/api.types';

export type TFabricVariant = 'KNIT' | 'WOVEN';
export type TTemplateVariant = `${TFabricVariant}_TEMPLATE`;
export enum TStockType {
  WITH_BCTX = 'with_bctx',
  WITH_ITEMS = 'with_items',
}
export type TAPIStatus = 'SUCCESS' | 'FAILURE';

/**
 * API 1: Generate Lot Template
 */

export type TGenerateTemplateAPIRequest = {
  variant: TTemplateVariant;
  factoryId: string;
  stockType?: TStockType;
};

/**
 * API 2: Upload Fabric Excel
 */

export type TLotUploadFormRequest = {
  factoryId: string;
  lotSheet?: File;
  filename?: string;
  uploadedBy: string;
  templateVariant?: TTemplateVariant;
  stockType?: TStockType;
};

export type TLotUploadResponse = {
  totalProcessedRows: number;
  successfulRows: number;
  failedRows: number;
  errors: number;
  errorFileKeyOpt: string;
};

/**
 * API 3. Update Lot
 */

export type TLotUpdateRequest = {
  factoryId: string;
  lotId: string;
  supplierName: string;
  supplierPo: string;
  fabricType: string;
  lotNo: string;
  fabricDescription?: string; //optional
  materialCode?: string; //optional
};

export type TLotUpdateResponse = {
  status: TAPIStatus;
  errors: string[];
};

/**
 * API 4. Update Roll
 */
export type TRollUpdateRequestForWOVEN = {
  rollId: string;
  rollNo: string;
  width: number;
  shade: string;
  length: number;
  gsm?: number;
  weight?: number;
  weftShrinkage?: number;
  warpShrinkage?: number;
};

export type TRollsUpdateRequest = {
  factoryId: string;
  lotId: string;
  updateRollRequests: TRollUpdateRequestForKNIT[] | TRollUpdateRequestForWOVEN[];
  stockType?: TStockType;
};

export type TRollUpdateRequestForKNIT = {
  rollId: string;
  rollNo: string;
  width: number;
  shade: string;
  gsm: number;
  weight: number;
  length?: number;
  weftShrinkage?: string;
  warpShrinkage?: string;
};

export type TRollsUpdateResponse = {
  status: TAPIStatus;
  topLevelError: string;
  responses: TRollUpdateResponse[];
};

export type TRollUpdateResponse = {
  rollId: string;
  status: TAPIStatus;
  errors: string[];
};

/**
 * API 5: Delete Lot
 */

export type TDeleteLotRequest = {
  factoryId: string;
  lotIdList: string[];
};
export type TDeleteLotItemResponse = {
  lotId: string;
  status: TAPIStatus;
  errors: string[];
};

export type TDeleteLotResponse = {
  responseList: TDeleteLotItemResponse[];
};

/**
 * API 6: Delete Rolls
 */

export type TDeleteRollsRequest = {
  rollIdList: string[];
  factoryId: string;
};

export type TDeleteRollResponse = {
  responseList: TRollUpdateResponse[];
};

/**
 * API 7: Add new Roll
 */

export type TRollAddRequest = {
  factoryId: string;
  baseLotId: string;
  rollNo: string;
  shade: string;
  width: number;
  length?: number;
  gsm?: number;
  weight?: number;
  weftShrinkage?: number;
  warpShrinkage?: number;
  splitLength?: number;
  factoryRollNo?: string;
  bookingQuantity?: string;
  receivedQuantity?: string;
  siloNo?: string;
  stockType?: TStockType;
};

export type TRollAddResponse = {
  status: TAPIStatus;
  errors: string[];
};

/**
 * Temperature and Humidity
 */

export type TTempUnit = 'C';

export type TGetTempHumidityRequest = {
  factoryId: string;
  dateRange: string[];
};

export type TGetTempHumidityResponse = {
  tempHumidityRecords: TTempHumidityDTO[];
  tempUnit: TTempUnit;
};

type TTempHumidityDTO = {
  round: number;
  date?: string;
  deptWiseRecords: DeptWiseRecord[];
};

type DeptWiseRecord = {
  deptId: string;
  deptName: string;
  temperature: number;
  humidity: number;
};

export type TTempHumidityUpdateRequest = {
  factoryId: string;
  date: string;
  round: number;
  deptWiseInfo: TDeptWiseInfo[];
};

type TDeptWiseInfo = {
  deptId: string;
  temperature: number;
  humidity: number;
};

export type TTempHumidityUpdateResponse = {
  status: TAPIStatus;
  errors: string[];
};

export type TempHumidityClearRoundRequest = {
  factoryId: string;
  rounds: number[];
};

export type TTempHumidityConfigResponse = {
  departments: TDepartmentConfig[];
  numRounds: number;
  tempUnit: TTempUnit;
  validTempRange: TTempRange;
  validHumidityRange: THumidityRange;
};

type TTempRange = {
  minTemp: number;
  maxTemp: number;
};

type THumidityRange = {
  minHumidity: number;
  maxHumidity: number;
};

type TDepartmentConfig = {
  deptId: string;
  deptName: string;
};

export type TTempHumidityClearRoundRequest = {
  date: string;
  factoryId: string;
  rounds: number[];
};

export type TTempHumidityClearRoundResponse = {
  status: TAPIStatus;
  topLevelError: string;
  responses: [{ round: number; status: TAPIStatus; error: string }];
};
//TODO move API response

/**
 * Tag Module APIs
 */
export type TTagTypes = 'BUNDLE_TAG' | 'PIECE_TAG' | 'COMPONENT_TAG';

export type TCutBundleFilterResponse = TCutBundleFilter[];

type TCutBundleFilter = {
  key: string;
  values: string;
};

export type TBundleCount = number;
export type TCutBundles = TCutBundle[];

export type TCutBundle = {
  bundleGuideType: string;
  tagType: string;
  tagGenId: string;
  cutNo: string;
  numericCutNumber: number;
  recut: boolean;
  buyer: string;
  po: string;
  order_ref: string;
  product_type: string;
  style: string;
  color: string;
  destination: string;
  tolerance: string;
  shrinkage: string;
  pcNo: string;
  sortNo: string;
  factory: string;
  guideVersion: number;
  bundleGuideQuantity: number;
  auxiliaryFields: TAuxiliaryFields;
  cutTagPrintStatus: string;
  pieceTagPrintStatus: string;
  pieceTagGenerationStatus: string;
  componentTagPrintStatus: string;
  componentTagGenerationStatus: string;
  valid: boolean;
  deleted: boolean;
  onlyTagReissue: boolean;
  subject_key: string;
  date_time: number;
  uploadSource: TUploadSource;
  file_id: string;
};

export type TAuxiliaryFields = {};

export type TUploadSource = {
  sourceType: string;
  sourceId: string;
  sourceName: string;
};

export type TOrderFilterRequest = {
  filterKeys: string[];
  selectedFilters: { [key in string]: string[] };
  factoryId: string;
};

export type TFilter = {
  key: string;
  values: string[];
  filterType: string;
};

export type TOrderRequest = {
  factoryId: string;
  filters: TFilter[];
  page: number;
  pageSize: number;
};

export type TFilterTypes = 'REGEX_FILTER' | 'EXACT_MATCH_FILTER';

export type TBundleGuideRequest = {
  subject_key: string;
  startDate: number;
  endDate: number;
  pageNumber: number;
  itemCount: number;
};

/** Order Data Response **/
export class OrderRequestResponse {
  totalRecords: number;
  orderItems: TOrderItems[];
  supportedFilters: TSupportedFilterItem[];

  constructor(request: { totalRecords: number; orderItems: TOrderItems[]; supportedFilters: TSupportedFilterItem[] }) {
    this.totalRecords = request.totalRecords ? request.totalRecords : 0;
    this.orderItems = request.orderItems ? request.orderItems : [];
    this.supportedFilters = request.supportedFilters ? request.supportedFilters : [];
  }
}

export type TOrderItems = {
  tagGenerationHash: string;
  baseOrderItemIds: any[];
  orderFields: TOrderField[];
  // sizeDetails: TOrderSizeData;
  orderStatusInfo: TOrderStatusInfo;
  quantity: number;
  sizeDetailsV2: TOrderSizeData;
};

export type TOrderField = {
  key: string;
  displayKeyName: string;
  value: string;
};

export type TOrderSizeData = {
  key: string;
  displayKeyName: string;
  // sizeById: any;
  sizeInfo: {
    orderItemId: string;
    size: string;
  }[];
};

export type TOrderStatusInfo = {
  status: TOrderStatus;
  actualProduced: any;
  editType: string;
};

export type TOrderStatus = {
  status: string;
  displayName: string;
};

export type TSupportedFilterItem = {
  fieldKey: string;
  filterType: string;
};

/** Bundle Guide / Filters request **/
export type TGetBundleGuideDataRequest = {
  subject_key: string;
  startDate: number;
  endDate: number;
  pageNumber: number;
  itemCount: number;
  filters: TBundleFilter[];
  bundleGuideType: string;
  onlyTagReissue: boolean;
  tagType: string;
};

export type TBundleFilter = {
  key: string;
  values: any[];
};

export type TBundleGuideFilterRequest = {
  subject_key: string;
  startDate: string;
  endDate: string;
  keys: string[];
  bundleGuideType: string;
  onlyTagReissue: boolean;
  tagType: string;
  filters: TBundleFilter[];
};

/** Bundle generation **/

export type TBundleGenerationRequest =
  | TBundleGenerationRequestNormal
  | TBundleGenerationRequestSizeWise
  | TBundleGenerationRequestStepWise;

export type TBundleGenerationRequestNormal = {
  subject_key: string;
  tagGenId: string;
  cutNo: number;
  recut: boolean;
  po: string;
  shades: { shade: string; numberOfPlies: number }[];
  sizes: { size: string; ratio: number; orderItemId: string }[];
  bundlingRule: number;
  bundleCreationLogic: string;
  bundlingMethod: 'NORMAL_CUTTING';
};

export type TBundleGenerationRequestSizeWise = {
  subject_key: string;
  tagGenId: string;
  cutNo: number;
  recut: boolean;
  po: string;
  sizeSteps: { size: string; orderItemId: string; bundlingRule: number; shade: string; pieces: number }[];
  bundleCreationLogic: string;
  bundlingMethod: 'SIZE_WISE_STEP_CUTTING';
};

export type TBundleGenerationRequestStepWise = {
  subject_key: string;
  tagGenId: string;
  cutNo: number;
  recut: boolean;
  po: string;
  shades: { shade: string; numberOfPlies: number }[];
  sizes: { size: string; ratio: number; orderItemId: string }[];
  bundlingRule: number;
  layerSteps: {
    bundlingRule: number;
    sizes: { size: string; ratio: number; orderItemId: string }[];
    shades: { shade: string; numberOfPlies: number }[];
  }[];
  bundleCreationLogic: string;
  bundlingMethod: 'LAYER_WISE_STEP_CUTTING_WITH_SIZE_BUNDLING' | 'LAYER_WISE_STEP_CUTTING_WITH_LAYER_BUNDLING';
};

/** cut no**/
export type TCutNoRequest = {
  subject_key: string;
  tagGenId: string;
};

export type TPrintTagRequest = {
  subject_key: string;
  tagGenId: string;
  cutNo: number;
  bundleNumbers: any[];
};

export type TPieceTagGenRequest = {
  subject_key: string;
  tagGenId: string;
  cutNo: number;
  pieces: any[];
};

export type TActivityLogRequest = {
  subject_key: string;
  cutNo: number;
  tagGenId: string;
};

export type TBundleGuideDepartmentSourceDTO = {
  dptId: string;
  dptName: string;
  wipType: string;
  wipName: string;
  subjectId: string;
  subjectName: string;
};

export class DepartmentSource {
  dptId: string;
  dptName: string;
  wipType: string;
  wipName: string;
  subjectId: string;
  subjectName: string;

  constructor(data: TDepartmentSource) {
    this.dptId = data.dptId;
    this.dptName = data.dptName;
    this.wipType = data.wipType;
    this.wipName = data.wipName;
    this.subjectId = data.subjectId;
    this.subjectName = data.subjectName;
  }
}

export type TCutBundleInDTO = {
  bundleNumber: number;
  size: string;
  orderItemId: string;
  ratio: number;
  shade: string;
  rollNo: string;
  bundleQuantity: number;
  stickerFrom: number;
  stickerTo: number;
};

export type TCutBundleGuideIdentifierV2 = {
  subject_key: string;
  cutBundleGuideId: string;
};

export type TCutBundleInputActionDTOV2 = {
  subject_key: string;
  cutBundleGuideId: string;
  cutBundles: TCutBundleInDTO[];
};

export type TCutBundleDeletionDTOV2 = {
  subject_key: string;
  cutBundleGuideId: string;
  bundleNumbers: number[];
};

export type TGetCutBundleRequestDTOV2 = {
  subject_key: string;
  cutBundleGuideId: string;
  tagType: TTagTypes;
};

export type TGeneratePieceTagRequestDTOV2 = {
  subject_key: string;
  cutBundleGuideId: string;
  pieces: TPieceDTO[];
};

export type TPrintTagRequestDTOV2 = {
  subject_key: string;
  cutBundleGuideId: string;
  bundleNumbers: number[];
};

export type TPrintPieceTagRequestDTOV2 = {
  subject_key: string;
  cutBundleGuideId: string;
  pieces: TPieceDTO[];
};

export type TPieceDTO = {
  bundleNumber: number;
  pieceNumber: number;
  lineNumber: string;
  stickerNumber: number;
};

export type TPieceTagHistoryDTO = {
  subjectKey: string;
  searchIds: string[];
};

export type TSizeListResponse = { [key: string]: string };

export type TCutBundlesForGuide = TCutBundleForGuide[];

export type TTransformedCutBundlesForGuide = (TCutBundleForGuide & {
  rfidTag: string;
  rfidMapped: boolean;
})[];

export type TRESPONSE_getCutBundleSForGuide = {
  cutBundle: TCutBundleForGuide;
  rfidTag: string;
  rfidMapped: boolean;
}[];

export type TCutBundleForGuide = {
  _id: string;
  bundleNumber: number;
  size: string;
  orderItemId: string;
  ratio: number;
  shade: string;
  bundleQuantity: number;
  stickerFrom: number;
  stickerTo: number;
  search_id: string;
  display_cut_tag: string;
  printed: boolean;
  deliveryDate: string;
  group: string;
};

export type TBundlesPrintAPIRespose = TBundlePrint[];

export type TBundlePrint = {
  search_id: string;
  bundleNumber: number;
};

export type TBundleDeleteAPIResponse = TBundleDeleteAPISucessResponse | TBundleDeleteAPIErrorResponse;

export type TBundleDeleteAPISucessResponse = {
  SUCCESS: string;
};

export type TBundleDeleteAPIErrorResponse = {
  ERROR: string;
};

export type TComponenetTypes = string[];

export type TComponentBundles = TComponentBundle[];

export type TComponentBundle = {
  cutBundleIds: string[];
  componentType: string;
  size: string;
  ratio: number;
  componentBundleNumber: number;
  bundleQuantity: number;
  stickerFrom: number;
  stickerTo: number;
  componentTag: string;
  displayComponentTag: string;
  printed: boolean;
};

export type TDepartmentAPIResponse = { result: TDepartment[] };

export type TDepartment = {
  dis_name: string;
  subject: string;
  name: string;
};

export type TBundleHostoryResponse = {
  fileId: string;
  subject_key: string;
  tagGenId: string;
  cutNo: string;
  numericCutNumber: number;
  bundleNumber: number;
  size: string;
  ratio: number;
  shade: string;
  rollNo: any;
  bundleQuantity: number;
  stickerFrom: number;
  stickerTo: number;
  search_id: string;
  createdAt: number;
  action: string;
}[];

export type TAddBundleGuideResponse = {
  cutBundleGuideId: string;
  cutBundles: TCutBundle[];
  componentBundles: TComponentBundle[];
  status: 'SUCCESS' | 'FAILURE';
  errorMessage: string;
};

export type TCutBundleGuideResponse = TCutBundleGuide[];

export type TCutBundleGuide = {
  subject_key: string;
  tagGenId: string;
  cutNo: number;
  recut: boolean;
  buyer: string;
  style: string;
  color: string;
  po: string;
  guideVersion: number;
  departmentSource: TDepartmentSource;
  cutTagPrintStatus: string;
  pieceTagPrintStatus: string;
  pieceTagGenerationStatus: string;
  valid: boolean;
  deleted: boolean;
  date_time: number;
  file_id: string;
};

export type TDepartmentSource = {
  dptId: string;
  dptName: string;
  wipType: string;
  wipName: string;
  subjectId: string;
  subjectName: string;
};

export type TWipDefinitionResponse = {
  result: [
    {
      list: { wip_name: string; wip_key: string; out: string[]; in: { common_subject: string }[] }[];
    },
  ];
};

export type TOrderFilterResponse = {
  orderFields: TOrderFields;
  minPlanDate: number;
  filterSource: string;
};

export type TOrderFields = {
  order_ref: string[];
  po: string[];
  color: string[];
  style: string[];
  buyer: string[];
  product_type: string[];
  product_category: string[];
};

export type TZipedResponse = number[];

export type TBundleGuidesForLaySheetResponse = {
  bundleNumber: number;
  size: string;
  orderItemId: string;
  ratio: number;
  shade: string;
  bundleQuantity: number;
  stickerFrom: number;
  stickerTo: number;
};

export type TSubjectsAPIResponse = { dis_name: string }[];

type TWipThresholdList = {
  floor: string;
  section: string;
  line: string;
  module_id: string;
  min_value: number;
  max_value: number;
  [key: string]: string | number;
};

export type TRESPONSE_GET_WIP_THRESHOLD = {
  header_info: { key: string; display_name: string }[];
  wip_thresholds_list: TWipThresholdList[];
  supported_filters: TFilterSelected[];
  selected_filters: TFilterSelected[];
  pagination_info: TPaginationInfo;
};

export type TREQUEST_GET_WIP_THRESHOLD = {
  page_no: number;
  page_size: number;
  selected_filters: TFilterSelected[];
};

export type TREQUEST_ADD_EDIT_WIP_THRESHOLD = {
  wip_thresholds_list: { module_id: string; min_value: number; max_value: number }[];
  source_type: 'ba-app';
};

export type TRESPONSE_ADD_EDIT_WIP_THRESHOLD = {
  status: string;
  message: string;
};

export type TResponseSchedulePlan = {
  index: number;
  _id: string;
  starttime: string;
  endtime: string;
  type: 'WORK' | 'BREAK';
  duration: number;
  sessionblock: number;
  startPlusDays: number;
  endPlusDays: number;
  cumulative_mins: number;
};

export type TResponsePieceTagHistory = {
  status: string;
  message: string | null;
  perPieceTagLatestStateWithTagDetails: TPieceTagLatestStateDetails[];
};

export type TPieceTagLatestStateDetails = {
  searchId: string;
  perPieceTagLatestState: {
    department: string;
    domRole: string;
    domRoleDn: string;
    eventName: string; //ftt, defectives, reject, reworked, defectives_r, reject_r
    createdAt: string;
    subjectDn: string;
  } | null;
};

export type TagRfidMapping = {
  searchId: string;
  rfidTag: string;
};

export type TREQUEST_MAP_RFID_COMPONENT_TAGS = {
  subjectKey: string;
  tagRfidMappingList: TagRfidMapping[];
  bundleTagDisplayId: string;
};

export type TREQUEST_MAP_RFID_BUNDLE_TAGS = {
  tagRfidMapping: TagRfidMapping;
  subjectKey: string;
};

export type TRESPONSE_MAP_RFID_TAGS = {
  status: TAPIStatus;
  error: string | null;
};
